import { Colors } from "@/enums/Colors";
import { LazoCompany } from "@/models/LazoCompany";
import { LazoUser } from "@/models/LazoUser";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import LazoUserService from "@/services/LazoUserService";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { Box, CircularProgress, IconButton, Modal, styled, TablePagination, Typography } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { LazoPortalChatProvider } from "./PortalChatContext";
import PortalInboxSavedStatistics from "./PortalInboxSavedStatistics";
import PortalInboxStatisticsFilters, { ToHandle } from "./PortalInboxStatisticsFilters";
import PortalInboxStatisticsReadConversation from "./PortalInboxStatisticsReadConversation";
import PortalInboxStatisticsTable from "./PortalInboxStatisticsTable";

type Props = {
  open: boolean;
  onClose: () => void;
};

type BasicWithName = {
  name: string;
  externalId: string;
};

export type Criteria = {
  startups: Partial<LazoCompany>[];
  lazoUsers: Partial<LazoUser>[];
  clientUsers: Partial<LazoUser>[];
  kams: Partial<LazoUser>[];
  status: BasicWithName[];
};

const InitialFilters = {
  startups: [],
  lazoUsers: [],
  clientUsers: [],
  kams: [],
  status: [],
};

export type StatisticsBody = {
  tenantExternalIds: string[];
  employeesExternalIds: string[];
  clientExternalIds: string[];
  kamExternalIds: string[];
  status: string[];
};

export type Conversation = {
  externalId: string;
  kam: { externalId: string; firstName: string; lastName: string };
  tenant: { name: string; externalId: string };
  members: { externalId: string; firstName: string; lastName: string; internal: boolean }[];
  status: string;
  waitingResponse: boolean;
  timeSinceOpen: number;
  timeWithNoResponse: number;
};

export function PortalInboxStatisticsModal(props: Props) {
  const [criteria, setCriteria] = useState<Criteria>(InitialFilters);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedConversation, setSelectedConversation] = useState<Conversation>();
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageMeta, setPageMeta] = useState<{ size?: number; totalElements?: number; totalPages?: number }>({});
  const [clientUsers, setClientUsers] = useState<Partial<LazoUser>[]>([]);

  useEffect(() => {
    if (!props.open) {
      setCriteria(InitialFilters);
      setSelectedConversation(null);
      setPageNumber(0);
      setPageMeta({});
    }
  }, [props.open]);

  useEffect(() => {
    LazoUserService.getLazoClientsByTenant(criteria.startups.map((s) => s.externalId)).then((r) => {
      r.onSuccess(() => {
        const data = r.getContentOrThrowError();
        setClientUsers(data.map(({ user }) => ({ externalId: user.externalId, name: user.firstName + " " + user.lastName })));
      });
    });
  }, [criteria.startups]);

  useEffect(() => {
    if (Object.values(criteria).some((c) => c && c.length > 0)) {
      setLoading(true);
      LazoChatConversationService.getStatistics(
        {
          tenantExternalIds: criteria.startups.map((s) => s.externalId),
          employeesExternalIds: criteria.lazoUsers.map((lu) => lu.externalId),
          clientExternalIds: criteria.clientUsers.map((cu) => cu.externalId),
          kamExternalIds: criteria.kams.map((k) => k.externalId),
          status: criteria.status.map((s) => s.externalId),
        },
        pageNumber
      )
        .then((r) => {
          r.onSuccess(() => {
            const data = r.getContentOrThrowError();
            setConversations(data.content);
            setPageMeta({
              size: data.page.size,
              totalElements: data.page.totalElements,
              totalPages: data.page.totalPages,
            });
          });
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setConversations([]);
      setPageMeta({});
    }
  }, [criteria, pageNumber]);

  // Wrap setCriteria so every criteria change (including saved-filter clicks) resets pagination.
  // Without this, applying a new filter while on page N would fetch page N of the new result set
  // — which is often empty, triggering the "No results found" state.
  const applyCriteria = (c: Criteria) => {
    setCriteria(c);
    setPageNumber(0);
  };

  const handleChange = (field: string, selected: ToHandle[]) => {
    setPageNumber(0);
    if (field === "startups" && selected.length > 0) {
      const startupIds = selected.map((startup) => startup.externalId);
      LazoUserService.getLazoClientsByTenant(startupIds).then((r) => {
        r.onSuccess(() => {
          const data = r.getContentOrThrowError();
          const externalIds = data.map((d) => d.user.externalId);
          const newClientUsers =
            criteria.clientUsers.length > 0 ? criteria.clientUsers.filter((u) => externalIds.includes(u.externalId)) : criteria.clientUsers;
          setCriteria({
            ...criteria,
            [field]: selected,
            clientUsers: newClientUsers,
          });
        });
      });
    } else {
      setCriteria({ ...criteria, [field]: selected });
    }
  };

  return (
    <>
      <LazoPortalChatProvider>
        <Modal open={props.open} onClose={props.onClose} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ModalContent>
            <ModalHeader>
              <Box display="flex" alignItems="center" gap="0.5rem">
                {selectedConversation && (
                  <IconButton size="small" onClick={() => setSelectedConversation(null)} sx={{ color: Colors.LIGHT_GRAY_8A8E93 }}>
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                )}
                <Typography fontWeight={600} fontSize="1rem" color={Colors.BLACK_151517}>
                  {selectedConversation ? "Conversation detail" : "Inbox Statistics"}
                </Typography>
              </Box>
              <IconButton size="small" onClick={props.onClose} sx={{ color: Colors.LIGHT_GRAY_8A8E93 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </ModalHeader>

            <ModalBody>
              {selectedConversation && <PortalInboxStatisticsReadConversation selectedConversation={selectedConversation} />}
              {!selectedConversation && (
                <>
                  <PortalInboxStatisticsFilters criteria={criteria} handleChange={handleChange} clientUsers={clientUsers} />
                  <PortalInboxSavedStatistics setCriteria={applyCriteria} criteria={criteria} />
                  <LoadingComponent loading={loading}>
                    <>
                      {conversations.length > 0 ? (
                        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                          <Box sx={{ flex: 1, overflow: "auto" }}>
                            <PortalInboxStatisticsTable conversations={conversations} setSelectedConversation={setSelectedConversation} />
                          </Box>
                          <Box sx={{ borderTop: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`, display: "flex", justifyContent: "flex-end" }}>
                            <TablePagination
                              rowsPerPageOptions={[]}
                              component="div"
                              count={pageMeta.totalElements ?? 0}
                              rowsPerPage={pageMeta.size ?? 15}
                              page={pageNumber}
                              onPageChange={(_, page) => setPageNumber(page)}
                              onRowsPerPageChange={() => {}}
                            />
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1 }}>
                          <Typography fontSize="0.875rem" color={Colors.LIGHT_GRAY_8A8E93} fontWeight={500}>
                            No results found. Try adjusting the filters.
                          </Typography>
                        </Box>
                      )}
                    </>
                  </LoadingComponent>
                </>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </LazoPortalChatProvider>
    </>
  );
}

const ModalContent = styled(Box)(() => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "white",
  borderRadius: "0.75rem",
  height: "90vh",
  width: "95vw",
  overflow: "hidden",
}));

const ModalHeader = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "1rem 1.25rem",
  borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  flexShrink: 0,
}));

const ModalBody = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  padding: "1.25rem",
  gap: "1rem",
  overflow: "auto",
}));

const LoadingComponent = ({ loading, children }: { loading: boolean; children: ReactNode }) => {
  return loading ? (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, gap: "0.75rem" }}>
      <CircularProgress size={18} sx={{ color: Colors.LAZO_PURPLE }} />
      <Typography fontSize="0.875rem" color={Colors.LIGHT_GRAY_8A8E93} fontWeight={500}>
        Loading...
      </Typography>
    </Box>
  ) : (
    <>{children}</>
  );
};
